import "@fontsource/unbounded/400.css";
import "@fontsource/unbounded/500.css";
import "@fontsource/unbounded/600.css";
import "@fontsource/unbounded/700.css";
import "@fontsource/plus-jakarta-sans/400.css";
import "@fontsource/plus-jakarta-sans/500.css";
import "@fontsource/plus-jakarta-sans/600.css";
import "@fontsource/plus-jakarta-sans/700.css";
import "@/App.css";

import React, { useEffect } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantProvider, useTenant } from "./contexts/TenantContext";
import { SocketProvider } from "./contexts/SocketContext";
import { Toaster } from "./components/ui/sonner";
import { initMobileEnvironment, teardownMobileEnvironment } from "./lib/mobile";

// Public Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ShopRegisterPage from "./pages/ShopRegisterPage";
import ShopWelcomePage from "./pages/ShopWelcomePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import BuildInfoPage from "./pages/BuildInfoPage";

// Route Modules
import { PlayerRoutes } from "./routes/playerRoutes";
import { ShopRoutes } from "./routes/shopRoutes";
import { AdminRoutes } from "./routes/adminRoutes";
import { PublisherRoutes } from "./routes/publisherRoutes";
import { Logo } from "./components/Logo";
import EulaConsentModal, { EULA_CURRENT_VERSION } from "./components/EulaConsentModal";

const LoadingScreen = () => (
  <div className="min-h-screen bg-[#06060A] flex items-center justify-center" data-testid="cyboard-splash">
    <div className="flex flex-col items-center gap-3 animate-pulse">
      <Logo variant="icon" size="xl" clickable={false} />
      <Logo variant="wordmark" size="md" clickable={false} />
      <p className="text-[#555] text-[10px] tracking-[0.24em] uppercase mt-1">Play · Connect · Level Up</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  // Don't auto-redirect — login/register pages handle their own navigation
  return children;
};

function RootRedirect() {
  const { activeContext, loading } = useTenant();
  const { user, loading: authLoading } = useAuth();

  if (authLoading || loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  // Admin role goes to /admin regardless of RBAC context state — defensive against
  // missing/stale rbac_active_context on production.
  if (user.role === 'admin' || activeContext?.tenant_type === 'internal') return <Navigate to="/admin" replace />;
  if (activeContext?.tenant_type === 'shop' && activeContext?.entity_id) return <Navigate to={`/shop/${activeContext.entity_id}`} replace />;
  if (activeContext?.tenant_type === 'publisher' && activeContext?.entity_id) return <Navigate to={`/publisher/${activeContext.entity_id}`} replace />;
  // Home is the default landing page for players — dashboard, upcoming events,
  // recommendations, and notifications all in one place. Discovery is still
  // one tap away from the bottom nav.
  return <Navigate to="/home" replace />;
}

function LegacyShopRedirect() {
  const shopId = window.location.pathname.split('/shop-portal/')[1]?.split('/')[0];
  return <Navigate to={`/shop/${shopId || ''}`} replace />;
}

function MobileInit() {
  const navigate = useNavigate();
  useEffect(() => {
    initMobileEnvironment(navigate);
    return () => { teardownMobileEnvironment(); };
  }, [navigate]);
  return null;
}

/**
 * EULA Gate — shown after a user is authenticated if their stored
 * `eula_version` doesn't match the current one (or is missing). Required by
 * Apple App Store Guideline 1.2 / 3.1.2(a) for UGC apps.
 *
 * NATIVE BEHAVIOR: on iOS/Android we *also* gate on a per-device localStorage
 * flag (`cyb_eula_seen_in_app`). This guarantees the modal shows the first
 * time you sign in on the phone, even if the same account previously accepted
 * the EULA on the web. Apple's review team will see it on a fresh install,
 * and your real users will see it once per device install.
 */
function EulaGate() {
  const { user, updateUser, logout } = useAuth();
  if (!user) return null;
  const accountAccepted = user.eula_version === EULA_CURRENT_VERSION;
  const isNative = typeof window !== 'undefined' && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  const deviceSeen = (() => {
    try { return localStorage.getItem('cyb_eula_seen_in_app') === EULA_CURRENT_VERSION; } catch { return false; }
  })();
  // Account-side: must be accepted in DB. Native: must ALSO have been confirmed
  // on this device. Web: account-side is enough.
  const needsAcceptance = !accountAccepted || (isNative && !deviceSeen);
  if (!needsAcceptance) return null;
  return (
    <EulaConsentModal
      open
      onAccept={(data) => {
        try { localStorage.setItem('cyb_eula_seen_in_app', data.eula_version); } catch { /* ignore */ }
        updateUser({ eula_version: data.eula_version, eula_accepted_at: data.eula_accepted_at });
      }}
      onDecline={() => logout()}
    />
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/register-shop" element={<PublicRoute><ShopRegisterPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/build-info" element={<BuildInfoPage />} />

      {/* Shop welcome (post-signup) — auth-required */}
      <Route path="/shop-welcome/:shopId" element={<ProtectedRoute><ShopWelcomePage /></ProtectedRoute>} />

      {/* Root Redirect */}
      <Route path="/" element={<ProtectedRoute><RootRedirect /></ProtectedRoute>} />

      {/* Player Routes */}
      {PlayerRoutes({ ProtectedRoute })}

      {/* Legacy Redirect */}
      <Route path="/shop-portal/:shopId" element={<ProtectedRoute><LegacyShopRedirect /></ProtectedRoute>} />

      {/* Shop Portal */}
      {ShopRoutes({ ProtectedRoute })}

      {/* Publisher Portal */}
      {PublisherRoutes({ ProtectedRoute })}

      {/* Admin Console */}
      {AdminRoutes({ ProtectedRoute })}

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <SocketProvider>
          <BrowserRouter>
            <MobileInit />
            <AppRoutes />
            <EulaGate />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </SocketProvider>
      </TenantProvider>
    </AuthProvider>
  );
}

export default App;
